/**
 * Authentication utilities for JWT token handling
 */

interface JWTPayload {
  userId: string;
  email: string;
  name?: string;
  photoUrl?: string;
  workerId?: string;
  exp?: number;
  iat?: number;
}

/**
 * Decode JWT token without verification (client-side only)
 * For actual verification, the server handles it
 */
export function decodeJWT(token: string): JWTPayload | null {
  try {
    // JWT structure: header.payload.signature
    const parts = token.split('.');
    if (parts.length !== 3) {
      return null;
    }

    // Decode the payload (second part)
    const payload = parts[1];
    const decoded = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(decoded);
  } catch (error) {
    console.error('Failed to decode JWT:', error);
    return null;
  }
}

/**
 * Get current user information from stored JWT token
 */
export function getCurrentUser(): JWTPayload | null {
  const token = localStorage.getItem('bearer_token');
  if (!token) {
    return null;
  }
  
  const decoded = decodeJWT(token);
  
  // Check if token is expired
  if (decoded?.exp) {
    const now = Math.floor(Date.now() / 1000);
    if (decoded.exp < now) {
      console.warn('JWT token has expired');
      // Don't remove it - let the server handle it
    }
  }
  
  return decoded;
}

/**
 * Get formatted user display name
 */
export function getUserDisplayName(user: JWTPayload | null): string {
  if (!user) {
    return 'VM User';
  }
  
  return user.name || user.email || user.userId || 'VM User';
}

/**
 * Get user email or fallback
 */
export function getUserEmail(user: JWTPayload | null): string {
  if (!user) {
    return 'user@vm.local';
  }
  
  return user.email || 'user@vm.local';
}

/**
 * Get user mode description
 */
export function getUserMode(user: JWTPayload | null): string {
  if (!user) {
    return 'Single User Mode';
  }
  
  if (user.workerId) {
    return `Worker: ${user.workerId}`;
  }
  
  return 'Authenticated User';
}

/**
 * Check if user is authenticated
 */
export function isAuthenticated(): boolean {
  const user = getCurrentUser();
  if (!user) {
    return false;
  }
  
  // Check expiry
  if (user.exp) {
    const now = Math.floor(Date.now() / 1000);
    return user.exp > now;
  }
  
  return true;
}